import { useState } from "react";
import { useNavigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { useDashboard } from "@/contexts/dashboard-context";
import { formatCurrency, formatHoursCompact } from "@/lib/format";
import { ChevronRight, ChevronDown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export function WeeklySnapshot() {
  const navigate = useNavigate();
  const { weeklySnapshot, rollingHours, isLoading } = useDashboard();
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return <WeeklySnapshotSkeleton />;
  }

  const {
    dailyEarnings,
    totalEarnings,
    hourlyRate,
    weeklyGoal,
    blockCount: backendBlockCount,
    totalHours,
    totalTips,
    packagesPerHour,
    perMileRate,
  } = weeklySnapshot;
  const { hoursAvailable } = rollingHours;

  // Find max earnings for bar scaling
  const maxEarnings = Math.max(...dailyEarnings.map((d) => d.earnings), 1);
  const hasEarnings = dailyEarnings.some((d) => d.earnings > 0);

  // Goal progress
  const goalSet = weeklyGoal !== null && weeklyGoal > 0;
  const goalReached = goalSet && totalEarnings >= weeklyGoal;

  // Block count fallback
  const blockCount = backendBlockCount > 0
    ? backendBlockCount
    : dailyEarnings.filter((d) => d.earnings > 0).length;

  // Per block calculation
  const perBlock = blockCount > 0 ? totalEarnings / blockCount : 0;

  const handleCardClick = () => {
    navigate("/weekly");
  };

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* Main clickable area - navigates to weekly */}
        <div
          className="p-4 space-y-3 cursor-pointer hover:bg-accent transition-colors"
          onClick={handleCardClick}
          role="link"
          aria-label="Weekly snapshot, tap for details"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">This Week</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>

          {/* Mini Bar Chart */}
          <div className="space-y-1">
            <div className="flex items-end justify-between gap-1 h-12">
              {dailyEarnings.map((day, index) => {
                // Calculate pixel height (max 48px = h-12)
                const heightPx = hasEarnings
                  ? Math.max((day.earnings / maxEarnings) * 48, day.earnings > 0 ? 6 : 4)
                  : 4;
                const isToday = index === getTodayIndex();

                return (
                  <div key={day.day} className="flex-1 flex items-end justify-center h-full">
                    <div
                      className={`w-2 rounded-sm transition-all ${
                        day.earnings > 0
                          ? isToday
                            ? "bg-primary ring-2 ring-primary/30"
                            : "bg-primary"
                          : "bg-muted"
                      }`}
                      style={{ height: `${heightPx}px` }}
                      aria-hidden="true"
                    />
                    <span className="sr-only">
                      {day.day}: {formatCurrency(day.earnings)}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Day labels */}
            <div className="flex justify-between text-[10px] text-muted-foreground">
              {dailyEarnings.map((day) => (
                <span key={day.day} className="flex-1 text-center">
                  {day.day.charAt(0)}
                </span>
              ))}
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-2 text-center pt-1">
            {/* Goal Progress */}
            <div>
              <p className={`text-sm font-semibold ${goalReached ? "text-green-600 dark:text-green-400" : ""}`}>
                {goalSet
                  ? `${formatCurrency(totalEarnings)} / ${formatCurrency(weeklyGoal)}`
                  : formatCurrency(totalEarnings)}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {goalSet ? "goal" : "earned"}
              </p>
            </div>

            {/* Hourly Rate */}
            <div>
              <p className="text-sm font-semibold">
                {hourlyRate > 0 ? `${formatCurrency(hourlyRate)}/hr` : "--/hr"}
              </p>
              <p className="text-[10px] text-muted-foreground">hourly</p>
            </div>

            {/* Hours Remaining */}
            <div>
              <p className={`text-sm font-semibold ${hoursAvailable <= 0 ? "text-destructive" : ""}`}>
                {formatHoursCompact(hoursAvailable)} left
              </p>
              <p className="text-[10px] text-muted-foreground">capacity</p>
            </div>
          </div>
        </div>

        {/* Expandable Stats Section */}
        <div
          className="border-t border-border cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={toggleExpanded}
        >
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Weekly Stats</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                expanded && "rotate-180"
              )}
            />
          </div>

          {/* Expandable content */}
          <div
            className={cn(
              "grid transition-all duration-200 ease-out",
              expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            )}
          >
            <div className="overflow-hidden">
              <div className="px-4 pb-4 grid grid-cols-4 gap-3">
                <StatBox value={blockCount > 0 ? blockCount.toString() : "--"} label="Blocks" />
                <StatBox value={totalHours > 0 ? `${totalHours}h` : "--"} label="Hours" />
                <StatBox value={perMileRate > 0 ? formatCurrency(perMileRate) : "--"} label="Per Mile" />
                <StatBox value={perBlock > 0 ? formatCurrency(perBlock) : "--"} label="Per Block" />
                <StatBox value={packagesPerHour > 0 ? packagesPerHour.toFixed(1) : "--"} label="Pkg/Hr" />
                <StatBox value={totalTips > 0 ? formatCurrency(totalTips) : "--"} label="Tips" />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatBox({ value, label }: { value: string; label: string }) {
  const isEmpty = value === "--";
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
        {isEmpty ? (
          <div className="w-4 h-0.5 bg-muted-foreground/50 rounded-full" />
        ) : (
          <span className="text-xs font-bold text-center leading-tight px-1">
            {value}
          </span>
        )}
      </div>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
}

function getTodayIndex(): number {
  const today = new Date().getDay();
  // Convert Sunday=0 to Monday=0 based index
  return today === 0 ? 6 : today - 1;
}

function WeeklySnapshotSkeleton() {
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-4" />
        </div>
        <div className="flex items-end justify-between gap-1 h-12">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="flex-1 h-full" />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 pt-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-3 w-10" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
